import { create } from 'zustand';
import { KeycloakUser, KeycloakProfile } from '@api/services/auth/types';

interface AuthState {
  // State
  user: KeycloakUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: Error | undefined;

  // Actions
  setUser: (user: KeycloakUser | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: Error | undefined) => void;
  clearAuth: () => void;

  // Computed getters
  getAccessToken: () => string | null;
  getProfile: () => KeycloakProfile | null;
  hasRole: (role: string) => boolean;
  isTokenExpired: () => boolean;
  getTokenExpiresAt: () => number | null;
}

const defaults = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: undefined,
};

export const useAuthStore = create<AuthState>((set, get) => ({
  ...defaults,

  // Actions
  setUser: (user: KeycloakUser | null) =>
    set(
      {
        user,
        isAuthenticated: !!user?.access_token,
        error: undefined, // Clear error on successful user set
      },
      false
    ),

  setLoading: (loading: boolean) => set({ isLoading: loading }, false),

  setError: (error: Error | undefined) => set({ error }, false),

  clearAuth: () =>
    set(
      {
        user: null,
        isAuthenticated: false,
        error: undefined,
      },
      false
    ),

  // Computed getters
  getAccessToken: () => {
    const { user } = get();
    return user?.access_token || null;
  },

  getProfile: () => {
    const { user } = get();
    return user?.profile || null;
  },

  hasRole: (role: string) => {
    const { user } = get();
    return user?.profile?.realm_access?.roles?.includes(role) || false;
  },

  isTokenExpired: () => {
    const { user } = get();
    return user?.expired || false;
  },

  getTokenExpiresAt: () => {
    const { user } = get();
    return user?.expires_at || null;
  },
}));

// Non-React access to the store
export const authStore = {
  getState: () => useAuthStore.getState(),
  subscribe: useAuthStore.subscribe,

  // Convenience methods for non-React usage
  isAuthenticated: () => useAuthStore.getState().isAuthenticated,
  getAccessToken: () => useAuthStore.getState().getAccessToken(),
  getProfile: () => useAuthStore.getState().getProfile(),
  hasRole: (role: string) => useAuthStore.getState().hasRole(role),
  getCurrentUser: () => useAuthStore.getState().user,
};
